import { inject, Injectable } from "@angular/core";
import { Params, Router } from "@angular/router";
import { JsonUtils } from "@ramudden/core/utils";
import { OrganizationApi } from "@ramudden/data-access/resource/organization.api";
import { UserApi } from "@ramudden/data-access/resource/user.api";
import { ProjectWebApi } from "@ramudden/data-access/resource/web";
import { MeasuringPointLinkStatus } from "@ramudden/models/map-filter";
import { AnalysisType } from "@ramudden/models/measuring-point";
import { MeasuringPointSearchParameters } from "@ramudden/models/web";
import { firstValueFrom, Subject } from "rxjs";
import { PersistFiltersService } from "../../../services/persist-filters.service";
import { FilterState, FilterStateChangedEventArgs } from "./filter-state";

export enum FilterServiceFields {
    analysisTypes = "types",
    linkStatus = "linkStatus",
    organizations = "organizations",
    projects = "projects",
    projectsForUserId = "projectsForUserId",
    search = "search",
    panAndZoom = "panAndZoom",
}

/**
 * Shared access to the current filter parameters.
 * For now this service is only used in this module, so it is not injected in root.
 *
 * Responsibilities:
 * * Share access to the current filter parameters
 * * Notify when the filter parameters change
 * * Remember the filter state between navigations
 * * Parse filters from QueryParameters and populate the UI
 */
@Injectable({
    providedIn: "root",
})
export class FilterService {
    private readonly organizationApi = inject(OrganizationApi);
    private readonly persistFilterService = inject(PersistFiltersService);
    private readonly projectApi = inject(ProjectWebApi);
    private readonly router = inject(Router);
    private readonly userApi = inject(UserApi);

    private previousFilterState = new FilterState(); // Keeps track of the previous value
    public filterState = new FilterState(); // Keeps track of the current value

    private filterStateChangedSubject = new Subject<FilterStateChangedEventArgs>(); // Not using BehaviorSubject because that is already triggered when subscribing
    public onFilterStateChanged$ = this.filterStateChangedSubject.asObservable(); // It is a best practice not to expose the subject directly

    notifyFilterStateChanged(forceZoomAndPan = false) {
        this.filterStateChangedSubject.next(
            new FilterStateChangedEventArgs(
                this.filterState,
                forceZoomAndPan ? forceZoomAndPan : this.shouldZoomToNewFilter(),
            ),
        );
        this.savePreviousFilterState();
    }

    private savePreviousFilterState() {
        this.persistFilterService.setFromString(this.filterState.toJsonString());
        this.previousFilterState = JsonUtils.deepClone(this.filterState);
    }

    private shouldZoomToNewFilter(): boolean {
        return (
            (!!this.filterState.organizations.length &&
                !this.filterState.organizations.areContentsEqual(
                    this.previousFilterState?.organizations,
                    (x) => x.id,
                )) ||
            (!!this.filterState.projects.length &&
                !this.filterState.projects.areContentsEqual(this.previousFilterState?.projects, (x) => x.id))
        );
    }

    // Convert our FE filter state in BE search parameters
    getSearchParameters(): MeasuringPointSearchParameters {
        const result = new MeasuringPointSearchParameters();
        if (this.filterState.projects.length) {
            result.projectIds = this.filterState.projects.map((x) => x.id);
        }

        if (this.filterState.types.length) {
            result.analysisTypeIds = this.filterState.types;
        }

        if (this.filterState.linkStatus === MeasuringPointLinkStatus.Linked) {
            result.hasLinkedDevice = true;
        } else if (this.filterState.linkStatus === MeasuringPointLinkStatus.NotLinked) {
            result.hasLinkedDevice = false; // Note that false has a different meaning than null here
        }

        if (this.filterState.organizations.length) {
            result.ownerIds = this.filterState.organizations.map((x) => x.id);
        }

        if (this.filterState.searchTerm) {
            result.search = this.filterState.searchTerm;
        }

        if (this.filterState.projectsForUserId) {
            result.projectsForUserId = this.filterState.projectsForUserId.id;
        }

        return result;
    }

    async initFromLocalStorage(params: Params): Promise<boolean> {
        this.filterState = FilterState.fromJsonString(this.persistFilterService.getAsString());

        const getValues = (valueInParams: any) => {
            return (valueInParams + "").split(",");
        };

        const organizationsParam = params[FilterServiceFields.organizations];
        if (organizationsParam) {
            const organizationIds = getValues(organizationsParam).map((x) => +x);

            for (const organizationId of organizationIds) {
                if (this.filterState.organizations.find((x) => x.id === organizationId)) continue;

                const organization = await firstValueFrom(this.organizationApi.get$(organizationId));
                this.filterState.organizations.push(organization);
            }
        }

        const projectsParams = params[FilterServiceFields.projects];
        if (projectsParams) {
            const projectIds = getValues(projectsParams).map((x) => +x);

            for (const projectId of projectIds) {
                if (this.filterState.projects.find((x) => x.id === projectId)) continue;

                const project = await this.projectApi.get(projectId);
                this.filterState.projects.push(project);
            }
        }

        const analysisTypeParams = params[FilterServiceFields.analysisTypes];
        if (analysisTypeParams) {
            const analysisTypes = getValues(analysisTypeParams).map((x) => <AnalysisType>x);

            for (const analysisType of analysisTypes) {
                if (this.filterState.types.contains(analysisType)) continue;

                this.filterState.types.push(analysisType);
            }
        }

        const hasLinkedDeviceParam = params[FilterServiceFields.linkStatus];
        if (hasLinkedDeviceParam) {
            this.filterState.linkStatus =
                hasLinkedDeviceParam === "true" ? MeasuringPointLinkStatus.Linked : MeasuringPointLinkStatus.NotLinked;
        }

        const searchParam = params[FilterServiceFields.search];
        if (searchParam) {
            this.filterState.searchTerm = searchParam;
        }

        const projectsForUserId = params[FilterServiceFields.projectsForUserId];
        if (projectsForUserId === "me") {
            this.filterState.projectsForUserId = await firstValueFrom(this.userApi.getSelf$());
        }

        const zoomAndPanParameter = params[FilterServiceFields.panAndZoom];
        const shouldZoom = zoomAndPanParameter ? zoomAndPanParameter === "true" : this.shouldZoomToNewFilter();

        this.router.navigate([], {});
        this.notifyFilterStateChanged(shouldZoom);
        return shouldZoom;
    }
}
